import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CreditCard, Lock, ExternalLink, Loader2, FlaskConical, Ticket, Gift, CheckCircle2, LogIn } from "lucide-react";

export default function Payment() {
  const [pkgs, setPkgs] = useState([]);
  const [picked, setPicked] = useState(null);
  const [mode, setMode] = useState("monthly"); // "monthly" | "full" (for pay_in_full packages)
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [params] = useSearchParams();
  const testMode = params.get("test") === "1";

  // Codes
  const [accessCode, setAccessCode] = useState("");
  const [refCode, setRefCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);

  useEffect(() => {
    api.get(`/packages${testMode ? "?test=1" : ""}`).then(r => {
      setPkgs(r.data);
      const saved = (typeof localStorage !== "undefined") ? localStorage.getItem("coachjp_pkg") : null;
      if (testMode) {
        const t = r.data.find(p => p.id === "test_rm1");
        setPicked(t ? t.id : (saved || r.data[0]?.id));
      } else {
        setPicked(saved || r.data.find(p => p.highlight)?.id || r.data[0]?.id);
      }
    });
  }, [testMode]);

  const startCheckout = async () => {
    if (!user) { toast.error("Please sign in or create an account first"); return; }
    if (!picked) return;
    setBusy(true);
    try {
      const cur = pkgs.find(p => p.id === picked);
      const m = cur?.pay_in_full ? mode : "monthly";
      const { data } = await api.post("/hitpay/create-payment", { package_id: picked, mode: m });
      if (data?.url) {
        toast.success("Redirecting to HitPay…");
        window.location.href = data.url;
      } else {
        toast.error("Could not start HitPay checkout");
        setBusy(false);
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Please sign in to complete your purchase");
        nav("/login?next=/payment");
      } else {
        toast.error(e.response?.data?.detail || "Payment failed");
      }
      setBusy(false);
    }
  };

  const redeemAccess = async () => {
    if (!accessCode.trim() || !user) return;
    setCodeBusy(true);
    try {
      const { data } = await api.post("/codes/redeem", { code: accessCode.trim().toUpperCase() });
      toast.success(`Access code accepted — ${data.package} unlocked!`);
      setAccessCode("");
      await refresh();
      nav("/app/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid access code");
    }
    setCodeBusy(false);
  };

  const applyRef = async () => {
    if (!refCode.trim() || !user) return;
    setCodeBusy(true);
    try {
      const { data } = await api.post("/referrals/apply", { code: refCode.trim().toUpperCase() });
      toast.success(`Invited by ${data.referrer_name} — you'll both get +1 free week on your first paid subscription.`);
      setRefCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid referral code");
    }
    setCodeBusy(false);
  };

  const current = pkgs.find(p => p.id === picked);

  // Show inline sign-in prompt if not authenticated
  if (!loading && !user) {
    return (
      <div className="min-h-screen bg-[#070707] text-white" data-testid="payment-not-authed">
        <PublicNav />
        <div className="max-w-md mx-auto px-6 py-16 text-center">
          <Lock className="w-12 h-12 text-[#d7ff00] mx-auto" />
          <h1 className="mt-4 font-display text-4xl uppercase">Sign in to <span className="text-[#d7ff00]">checkout</span></h1>
          <p className="mt-3 text-[#bbb] text-sm">Your subscription is linked to your account. Sign in or create one to continue.</p>
          <div className="mt-6 flex flex-col gap-3">
            <Link to="/login?next=/payment" className="bg-[#d7ff00] text-black font-display tracking-wider uppercase px-6 py-3 hover:bg-white flex items-center justify-center gap-2" data-testid="payment-signin-link">
              <LogIn className="w-4 h-4" /> Sign in
            </Link>
            <Link to="/register" className="border border-[#d7ff00] text-[#d7ff00] font-display tracking-wider uppercase px-6 py-3 hover:bg-[#d7ff00] hover:text-black" data-testid="payment-register-link">
              Create account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070707] text-white" data-testid="payment-page">
      <PublicNav />
      <div className="max-w-3xl mx-auto px-6 py-12 grid gap-8 md:grid-cols-[1fr_320px]">
        <div>
          <div className="font-mono-jp text-xs text-[#d7ff00] tracking-[0.3em] uppercase">CHECKOUT · HITPAY</div>
          <h1 className="mt-3 font-display text-5xl uppercase">Activate your <span className="text-[#d7ff00]">plan</span></h1>

          {testMode && (
            <div className="mt-4 bg-[#1a1f00] border border-[#d7ff00] px-4 py-3 flex items-center gap-3" data-testid="test-mode-banner">
              <FlaskConical className="w-5 h-5 text-[#d7ff00]" />
              <div>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">TEST MODE · RM 2</div>
                <div className="text-xs text-[#bbb]">Sandbox payment to validate the full HitPay flow. Refundable via HitPay dashboard after.</div>
              </div>
            </div>
          )}

          {/* Code redemption (access + referral) */}
          <div className="mt-6 bg-[#0a0a0a] border border-[#d7ff00]/40 p-5" data-testid="payment-codes-section">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">HAVE A CODE?</div>
            <p className="mt-1 text-[#bbb] text-xs">Got an access code from Coach JP or a referral code from a friend? Enter it here before paying.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1.5"><Ticket className="w-3 h-3" /> Access code</label>
                <input
                  value={accessCode}
                  onChange={e => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="CJP-XXXX-XXXX"
                  className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2.5 font-mono-jp tracking-wider text-sm"
                  data-testid="payment-access-code"
                />
                <button
                  onClick={redeemAccess}
                  disabled={codeBusy || !accessCode.trim()}
                  className="mt-2 w-full bg-[#d7ff00] text-black font-display tracking-wider uppercase py-2 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-1.5 transition-colors"
                  data-testid="payment-redeem-access"
                >
                  <Ticket className="w-3.5 h-3.5" /> Apply access code
                </button>
              </div>
              <div>
                <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1.5"><Gift className="w-3 h-3" /> Referral code</label>
                {user?.referred_by ? (
                  <div className="mt-2 bg-[#0e0e0e] border border-[#d7ff00]/40 px-3 py-2.5 inline-flex items-center gap-2 text-[#d7ff00] font-mono-jp text-[10px] uppercase tracking-widest w-full" data-testid="payment-ref-applied">
                    <CheckCircle2 className="w-4 h-4" /> Referral applied · +1 week reward on subscription
                  </div>
                ) : (
                  <>
                    <input
                      value={refCode}
                      onChange={e => setRefCode(e.target.value.toUpperCase())}
                      placeholder="FRIEND-XXXX"
                      className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2.5 font-mono-jp tracking-wider text-sm"
                      data-testid="payment-ref-code"
                    />
                    <button
                      onClick={applyRef}
                      disabled={codeBusy || !refCode.trim()}
                      className="mt-2 w-full bg-[#d7ff00] text-black font-display tracking-wider uppercase py-2 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-1.5 transition-colors"
                      data-testid="payment-apply-ref"
                    >
                      <Gift className="w-3.5 h-3.5" /> Apply referral
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-[#0a0a0a] border border-[#1f1f1f] p-5">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-[#d7ff00] mt-0.5" />
              <div>
                <div className="font-display text-xl uppercase">
                  {current?.pay_in_full && mode === "full" ? "Pay in Full · One-Time" : "Card · Secure HitPay Checkout"}
                </div>
                <p className="mt-1 text-sm text-[#bbb]">
                  {current?.pay_in_full && mode === "full"
                    ? "One-time payment — pay by card, FPX, DuitNow QR or e-wallet. No recurring charges. Your plan unlocks automatically once payment is confirmed."
                    : "Monthly coaching is paid by credit / debit card. You'll be redirected to HitPay to enter your card; your plan unlocks automatically once payment is confirmed."}
                </p>
              </div>
            </div>
            <ul className="mt-4 text-xs text-[#888] space-y-1 font-mono-jp">
              <li>· Buyer: <span className="text-white">{user?.email || "—"}</span></li>
              <li>· Merchant: <span className="text-white">Cryo-Fit Asia Sdn Bhd (via HitPay)</span></li>
              <li>· Auto-activation on confirmed payment</li>
            </ul>
          </div>

          <div className="mt-6 flex items-start gap-2 text-xs text-[#888]">
            <Lock className="w-3.5 h-3.5 mt-0.5" />
            Powered by HitPay. Coach JP never sees your card details. Payment is verified server-side via signed webhook before access is granted.
          </div>
        </div>

        <aside className="bg-[#0a0a0a] border border-[#1f1f1f] p-5 self-start">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Your Package</div>
          <select value={picked || ""} onChange={(e) => { setPicked(e.target.value); setMode("monthly"); }}
            className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2" data-testid="pay-pkg-select">
            {pkgs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {current && (
            <>
              {current.pay_in_full ? (
                <div className="mt-3 space-y-2" data-testid="pay-mode-choice">
                  <button onClick={() => setMode("monthly")} className={`w-full text-left border p-3 transition ${mode === "monthly" ? "border-[#d7ff00] bg-[#d7ff00]/5" : "border-[#222] hover:border-[#333]"}`} data-testid="pay-mode-monthly">
                    <div className="flex items-center justify-between">
                      <span className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Monthly Subscription</span>
                      <span className={`w-3 h-3 rounded-full border ${mode === "monthly" ? "bg-[#d7ff00] border-[#d7ff00]" : "border-[#444]"}`} />
                    </div>
                    <div className="mt-1 font-display text-2xl text-[#d7ff00]">{current.currency} {current.price}<span className="text-xs text-[#888] font-mono-jp"> {current.period}</span></div>
                    <div className="text-[10px] text-[#888] font-mono-jp uppercase tracking-widest">Credit / debit card</div>
                  </button>
                  <button onClick={() => setMode("full")} className={`w-full text-left border p-3 transition ${mode === "full" ? "border-[#d7ff00] bg-[#d7ff00]/5" : "border-[#222] hover:border-[#333]"}`} data-testid="pay-mode-full">
                    <div className="flex items-center justify-between">
                      <span className="font-mono-jp text-[10px] uppercase tracking-widest text-[#71c4ff]">Pay in Full · Best Value</span>
                      <span className={`w-3 h-3 rounded-full border ${mode === "full" ? "bg-[#d7ff00] border-[#d7ff00]" : "border-[#444]"}`} />
                    </div>
                    <div className="mt-1 font-display text-2xl text-[#d7ff00]">{current.currency} {current.pay_in_full}</div>
                    <div className="text-[10px] text-[#888] font-mono-jp uppercase tracking-widest">One-time · card, FPX, DuitNow, e-wallets</div>
                  </button>
                </div>
              ) : (
                <div className="mt-3 font-display text-3xl text-[#d7ff00]">{current.currency} {current.price}{current.period && <span className="text-sm text-[#888] font-mono-jp"> {current.period}</span>}</div>
              )}
              <ul className="mt-3 text-xs text-[#bbb] space-y-1">{(current.features || []).map((f, i) => <li key={i}>· {f}</li>)}</ul>
            </>
          )}
          <button onClick={startCheckout} disabled={busy || !picked} className="mt-5 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="pay-btn">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><ExternalLink className="w-4 h-4" /> Pay with HitPay</>}
          </button>
          <div className="mt-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#666] text-center">REDIRECTS TO HITPAY · AUTOMATIC ACTIVATION</div>
        </aside>
      </div>
    </div>
  );
}
